import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
import Draggable from 'gsap/Draggable';
import InertiaPlugin from 'gsap/InertiaPlugin';
import { onWidthResize, offWidthResize } from '../utils/resize.js';
gsap.registerPlugin(ScrollTrigger, SplitText, Draggable, InertiaPlugin);

// Slow drift, same idea as the Instagram row (px/sec).
const MARQUEE_SPEED = 24;
const DRAG_CLICK_THRESHOLD = 6;

export default class BlogHero {
    constructor(container) {
        this.container = container;
        this.title = this.container.querySelector('.blog-hero__title');
        this.viewport = this.container.querySelector('[data-blog-tags-viewport]');
        this.track = this.container.querySelector('[data-blog-tags-track]');
        this.items = this.track
            ? gsap.utils.toArray(this.track.querySelectorAll('.blog-hero__tag:not([data-marquee-clone])'))
            : [];
        this.count = this.container.querySelector('.blog-hero__count');

        this.clones = [];
        this.tween = null;
        this.draggable = null;
        this.guardAbort = null;
        this.setWidth = 0;

        // Build the marquee first: clones are made before the entrance tween
        // sets inline opacity, so they never inherit a hidden state.
        this.applyMode();
        this.init();
        this.focusActiveTag();

        onWidthResize(this.onResize);
        document.addEventListener('blog:tags-changed', this.onTagsChanged);
        this.container.addEventListener('click', this.onTagClick);
    }

    onTagClick = (e) => {
        const tag = e.target.closest?.('.blog-hero__tag');
        if (!tag || !this.container.contains(tag)) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('blog:filter', { detail: { url: tag.href } }));
    };

    // Ajax tag switch: blog-listing re-applies .is-active (clones included),
    // then tells us so the row can bring the selected category into view.
    onTagsChanged = () => {
        this.focusActiveTag();
    };

    focusActiveTag = () => {
        if (!this.setWidth) return;
        const active = this.track?.querySelector('.blog-hero__tag.is-active:not([data-marquee-clone])');
        if (!active) return;
        gsap.set(this.track, { x: this.wrapX(-active.offsetLeft) });
        this.startAutoplay();
    };

    /* ---------------------------------------------------------------- marquee */

    // Plain single row when everything fits; clone + loop + drag only when the
    // categories actually overflow the viewport box.
    applyMode = () => {
        this.teardownMode();
        if (!this.viewport || !this.track || this.items.length < 2) return;

        gsap.set(this.track, { clearProps: 'transform,x' });
        if (!this.overflows()) return;

        this.track.classList.add('is-marquee');
        this.fillOneCopy();
        this.setWidth = this.measureSetWidth();
        if (this.setWidth <= 0) {
            this.teardownMode();
            return;
        }

        this.startAutoplay();
        this.initDrag();
        this.bindDragClickGuard();
    };

    overflows = () => this.track.scrollWidth > this.viewport.clientWidth + 1;

    fillOneCopy = () => {
        this.clones = [];
        this.items.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.dataset.marqueeClone = 'true';
            clone.setAttribute('tabindex', '-1');
            clone.setAttribute('aria-hidden', 'true');
            this.track.appendChild(clone);
            this.clones.push(clone);
        });
    };

    measureSetWidth = () => {
        const first = this.items[0];
        const firstClone = this.clones[0];
        if (!first || !firstClone) return 0;
        return firstClone.offsetLeft - first.offsetLeft;
    };

    wrapX = (x) => {
        if (!this.setWidth) return 0;
        return gsap.utils.wrap(-this.setWidth, 0, x);
    };

    // Autoplay rides the shared gsap.ticker (synced with window.lenis in
    // main.js) — no extra RAF loop, no second Lenis.
    startAutoplay = () => {
        this.tween?.kill();
        if (!this.setWidth) return;
        gsap.set(this.track, { x: this.wrapX(gsap.getProperty(this.track, 'x')) });
        this.tween = gsap.to(this.track, {
            x: `-=${this.setWidth}`,
            duration: this.setWidth / MARQUEE_SPEED,
            ease: 'none',
            repeat: -1,
            modifiers: {
                x: (x) => `${this.wrapX(parseFloat(x))}px`,
            },
        });
    };

    initDrag = () => {
        this.draggable = Draggable.create(this.track, {
            type: 'x',
            inertia: true,
            dragClickables: true,
            minimumMovement: DRAG_CLICK_THRESHOLD,
            allowNativeTouchScrolling: true,
            cursor: 'grab',
            activeCursor: 'grabbing',
            zIndexBoost: false,
            liveSnap: {
                x: (value) => this.wrapX(value),
            },
            snap: {
                x: (value) => this.wrapX(value),
            },
            onPressInit: () => {
                this.tween?.pause();
            },
            onRelease: () => {
                if (!this.draggable?.isThrowing) this.startAutoplay();
            },
            onThrowComplete: () => {
                this.startAutoplay();
            },
        })[0];
    };

    onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            if (this.draggable?.isDragging || this.draggable?.isThrowing) return;
            this.applyMode();
        }, 150);
    };

    // A click that ends a drag must not navigate.
    bindDragClickGuard = () => {
        this.guardAbort?.abort();
        this.guardAbort = new AbortController();
        const { signal } = this.guardAbort;
        let startX = 0;
        let startY = 0;
        let pressed = false;
        let dragged = false;
        let clearTimer = 0;

        const setDragging = (value) => {
            dragged = value;
            this.track.classList.toggle('is-dragging', value);
        };

        this.track.addEventListener(
            'pointerdown',
            (e) => {
                clearTimeout(clearTimer);
                pressed = true;
                startX = e.clientX;
                startY = e.clientY;
                setDragging(false);
            },
            { signal }
        );

        const onMove = (e) => {
            if (!pressed || dragged) return;
            if (
                Math.abs(e.clientX - startX) > DRAG_CLICK_THRESHOLD ||
                Math.abs(e.clientY - startY) > DRAG_CLICK_THRESHOLD
            ) {
                setDragging(true);
            }
        };

        const endPress = () => {
            if (!pressed) return;
            pressed = false;
            if (!dragged) return;
            clearTimer = window.setTimeout(() => setDragging(false), 80);
        };

        window.addEventListener('pointermove', onMove, { capture: true, signal });
        window.addEventListener('pointerup', endPress, { capture: true, signal });
        window.addEventListener('pointercancel', endPress, { capture: true, signal });

        const blockOpen = (e) => {
            if (!dragged) return;
            if (!this.track.contains(e.target)) return;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        };

        this.container.addEventListener('click', blockOpen, { capture: true, signal });
    };

    teardownMode = () => {
        this.tween?.kill();
        this.tween = null;
        this.draggable?.kill();
        this.draggable = null;
        this.guardAbort?.abort();
        this.guardAbort = null;
        this.track?.classList.remove('is-dragging', 'is-marquee');
        if (this.clones.length) {
            this.clones.forEach((clone) => clone.remove());
            this.clones = [];
        }
        this.setWidth = 0;
    };

    /* -------------------------------------------------------------- entrance */

    init = () => {
        this.initTitleAnimation();
        this.initMetaAnimation();
    };

    initTitleAnimation = () => {
        if (!this.title) return;

        this.titleSplit = new SplitText(this.title, { type: 'lines,chars', linesClass: 'lineParent', charsClass: 'char' });

        gsap.fromTo(
            this.titleSplit.chars,
            { yPercent: 100 },
            {
                yPercent: 0,
                duration: 0.6,
                delay: 0.7,
                stagger: { each: 0.03, ease: 'power2.out', from: 'start' },
                ease: 'power3.out',
                scrollTrigger: { trigger: this.container },
                onComplete: () => {
                    onWidthResize(this.onTitleResize);
                },
            }
        );
    };

    onTitleResize = () => {
        this.titleSplit?.revert();
        offWidthResize(this.onTitleResize);
    };

    // Animates the tags themselves (opacity/y), never the track — the track
    // carries the Draggable/marquee transform.
    initMetaAnimation = () => {
        const targets = [...this.items, ...(this.count ? [this.count] : [])];

        if (!targets.length) return;

        gsap.fromTo(
            targets,
            { opacity: 0, yPercent: 60 },
            {
                opacity: 1,
                yPercent: 0,
                duration: 0.7,
                stagger: { each: 0.07, ease: 'power2.out', from: 'start' },
                ease: 'power3.out',
                scrollTrigger: { trigger: this.container },
                delay: 0.3,
            }
        );
    };
}
